import { ApplicationInsights } from '@microsoft/applicationinsights-web';
import { ReactPlugin } from '@microsoft/applicationinsights-react-js';
import { createBrowserHistory } from 'history';
import { isProduction, appInsightsConnectionString } from '../config';
import { Logger } from '../utils/Logger';

let appInsights: ApplicationInsights | null = null;
let reactPlugin: ReactPlugin | null = null;
const browserHistory = createBrowserHistory();

/**
 * Initialize Application Insights with React plugin
 * Should be called once on app startup before rendering
 */
export function initializeAppInsights(): ApplicationInsights | null {
  // Check if Application Insights is already initialized
  if (appInsights) {
    Logger.debug('AppInsights', 'Already initialized');
    return appInsights;
  }

  // Get connection string from config (loaded from settings.js by backend)
  const connectionString = appInsightsConnectionString;

  if (!connectionString) {
    console.warn('[AppInsights] No connection string configured, skipping initialization');
    return null;
  }

  try {
    // Initialize React plugin
    reactPlugin = new ReactPlugin();

    // Initialize Application Insights with React plugin configuration
    appInsights = new ApplicationInsights({
      config: {
        connectionString: connectionString,
        extensions: [reactPlugin],
        extensionConfig: {
          [reactPlugin.identifier]: {
            history: browserHistory
          }
        },
        enableAutoRouteTracking: true,
        autoTrackPageVisitTime: true,
        enableUnhandledPromiseRejectionTracking: true,
        enableCorsCorrelation: true,
        correlationHeaderExcludedDomains: [
          // Add any domains where you don't want correlation headers
        ],
        /* Set sample rate: 80% in prod, 0% in dev (dev telemetry pollutes prod dashboards) */
        samplingPercentage: isProduction ? 80 : 0,
      }
    });

    // Initialize the SDK
    appInsights.loadAppInsights();

    // Add global properties to all telemetry events
    // baseUrl identifies the environment (prod, test, dev) and deployment
    const baseUrl = window.location.origin;
    appInsights.addTelemetryInitializer((envelope) => {
      // Initialize data object if needed
      envelope.data = envelope.data || {};

      // Add baseUrl to all events via customDimensions
      envelope.data.baseUrl = baseUrl;

      return true;
    });

    Logger.debug('AppInsights', 'Initialized successfully (baseUrl: %s)', baseUrl);
    return appInsights;

  } catch (error) {
    Logger.error('AppInsights', 'Failed to initialize:', error);
    return null;
  }
}

/**
 * Get the initialized Application Insights instance
 * Returns null if not initialized or initialization failed
 */
export function getAppInsights(): ApplicationInsights | null {
  return appInsights;
}

/**
 * Get the React plugin for provider integration
 */
export function getReactPlugin(): ReactPlugin | null {
  return reactPlugin;
}

/**
 * Get the browser history instance for React Router integration
 */
export function getBrowserHistory() {
  return browserHistory;
}

/**
 * Track a custom event
 */
export function trackEvent(eventName: string, properties?: Record<string, string | number | boolean | undefined>) {
  if (!appInsights) {
    console.warn(`[AppInsights] trackEvent called before initialization: ${eventName}`);
    return;
  }

  // Filter out undefined properties
  const filteredProps = properties
    ? Object.entries(properties).reduce((acc, [key, value]) => {
        if (value !== undefined) {
          acc[key] = String(value);
        }
        return acc;
      }, {} as Record<string, string>)
    : undefined;

  appInsights.trackEvent({
    name: eventName,
    properties: filteredProps
  });
}

/**
 * Track an exception
 */
export function trackException(error: Error, properties?: Record<string, string | number | boolean>) {
  if (!appInsights) {
    console.warn('[AppInsights] trackException called before initialization');
    return;
  }

  appInsights.trackException({
    exception: error,
    properties: properties ? Object.entries(properties).reduce((acc, [key, value]) => {
      acc[key] = String(value);
      return acc;
    }, {} as Record<string, string>) : undefined
  });
}

/**
 * Track a metric
 */
export function trackMetric(name: string, average: number, properties?: Record<string, string | number | boolean>) {
  if (!appInsights) {
    console.warn(`[AppInsights] trackMetric called before initialization: ${name}`);
    return;
  }

  appInsights.trackMetric({
    name,
    average,
    properties: properties ? Object.entries(properties).reduce((acc, [key, value]) => {
      acc[key] = String(value);
      return acc;
    }, {} as Record<string, string>) : undefined
  });
}

/**
 * Set authenticated user context (use userId only, avoid PII)
 */
export function setAuthenticatedUserContext(userId: string) {
  if (!appInsights) {
    console.warn('[AppInsights] setAuthenticatedUserContext called before initialization');
    return;
  }

  appInsights.setAuthenticatedUserContext(userId, undefined, false);
}

/**
 * Clear authenticated user context (call on logout)
 */
export function clearAuthenticatedUserContext() {
  if (!appInsights) {
    return;
  }

  appInsights.clearAuthenticatedUserContext();
}

/**
 * Track user flow events to identify blocking points and abandonment
 * Helps detect where users get stuck or stop using the application
 *
 * Examples:
 * - trackUserFlow('LOGEMENT_EDIT_STARTED', { logementId: 123 })
 * - trackUserFlow('LOGEMENT_SAVE_FAILED', { logementId: 123, errorMessage: 'Network error' })
 * - trackUserFlow('FORM_ABANDONED', { formName: 'diagnosis', progressPercent: 50 })
 */
export function trackUserFlow(
  eventName: string,
  properties?: Record<string, unknown>
): void {
  if (!appInsights) {
    return;
  }

  appInsights.trackEvent({
    name: eventName,
    properties: {
      ...properties,
      timestamp: new Date().toISOString(),
      route: window.location.pathname
    }
  });
}

/**
 * Flush Application Insights queue (useful before page navigation)
 */
export function flushAppInsights() {
  if (!appInsights) {
    return;
  }

  appInsights.trackPageView();
}
